import type { RealtimeMessage } from '@going-once/contracts/channels';

/**
 * Subscribing to an AppSync Events channel, hand-rolled — **one implementation, two apps**.
 *
 * This was `apps/web/src/lib/realtime.ts`, a React hook with the protocol inside it. The
 * phone needs the same protocol, and the protocol is where every bug this file has had
 * lived: the HTTP host in the auth header, the batched `event` array, the keep-alive that
 * makes a quiet show distinguishable from a dead socket. Each of those cost a day once.
 * So the protocol moved here, framework-free, and each app keeps a ten-line hook over it.
 * (No React in this package: the mobile bundler resolves shared packages with hierarchical
 * lookup off, so a React import here could not resolve — the constraint draws the line in
 * the right place.)
 *
 * **Why not Amplify.** Its `events` client is the official way and it arrives with the
 * Amplify runtime behind it — a large dependency for a protocol that is a WebSocket, two
 * JSON messages and a subprotocol header. The same judgment as the router (D46).
 *
 * **The subscription is the fast path, never the source of truth** (D50). Every screen
 * that uses it keeps the read that served its first render, and reaches for it when a
 * `sequence` gap says a message was missed. This channel publishes at-most-once by design.
 *
 * **It never sends anything but protocol frames.** Clients do not publish (D50); the key
 * a client holds could not publish if it tried, because publish is IAM-only.
 */

/**
 * `unconfigured` is its own state, and it exists because of a wasted evening: a screen with
 * no realtime configuration opens no socket and works fine on its polling fallback, which
 * from the outside is indistinguishable from a socket that is connected and quiet.
 */
export type ChannelStatus = 'idle' | 'unconfigured' | 'connecting' | 'open' | 'error';

export interface ChannelState {
  status: ChannelStatus;
  /**
   * Whether this channel can be relied on to deliver — **the signal a caller slows its
   * polling against.** `subscribe_success` plus a keep-alive every sixty seconds, not "a
   * message arrived": a live show between lots is silent, and a rule keyed on messages
   * kept every healthy screen at the fallback poll rate forever.
   */
  live: boolean;
}

/** The three values `config.json` carries, in the shape both apps already resolve them to. */
export interface RealtimeEndpoint {
  /** `wss://…` — the socket is opened here. */
  url: string;
  /** `https://…` — every authorization object names this one, not the socket's host. */
  httpUrl: string;
  apiKey: string;
}

/**
 * The part of a WebSocket this file uses — declared rather than imported, because the
 * package compiles under three different libs (Node's, the DOM's and React Native's) and
 * each spells the type differently. Also what lets the spec drive the protocol by hand.
 */
export interface SocketLike {
  readonly readyState: number;
  send(data: string): void;
  close(): void;
  addEventListener(type: 'open', listener: () => void): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: () => void): void;
  addEventListener(type: 'close', listener: (event: { code?: number; reason?: string }) => void): void;
}

export type SocketFactory = (url: string, protocols: string[]) => SocketLike;

/** `WebSocket.OPEN` — a constant on every implementation, and the only state this reads. */
const OPEN = 1;

/**
 * How long without a frame before the channel is treated as dead. Keep-alives arrive every
 * sixty seconds; two and a half minutes is late enough not to trip on a slow one and early
 * enough that a caller's poll takes over before anybody notices the screen has stopped.
 */
export const KEEPALIVE_GRACE_MS = 150_000;

/**
 * AppSync carries the auth for connect and subscribe in a base64url subprotocol token.
 *
 * **The `host` is the HTTP endpoint, not the realtime one**, even though the socket is
 * opened against the realtime host — AppSync validates the connection against the HTTP
 * domain. Getting this wrong fails the handshake with a close rather than an error anybody
 * can read, so the screen silently stays on its polling fallback. It did, for a day.
 *
 * `btoa` and `TextEncoder` are globals in browsers, Node and Hermes alike.
 */
export function authProtocol(httpHost: string, apiKey: string): string {
  const header = JSON.stringify({ host: httpHost, 'x-api-key': apiKey });
  const base64 = btoa(String.fromCharCode(...new TextEncoder().encode(header)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `header-${base64}`;
}

/**
 * One socket, many channels — **a connection carries up to 200 subscriptions** (S2).
 *
 * A viewer watching a show holds two channels (bids and room) and they share this. Two
 * sockets would be two handshakes, two keep-alive timers, and two connections that fail
 * independently into a screen that is half live. Reference-counted: the first subscriber
 * opens the connection, the last one closes it.
 */
interface Connection {
  socket: SocketLike;
  httpHost: string;
  apiKey: string;
  subscribers: Map<string, (message: RealtimeMessage) => void>;
  /** Channels waiting for `connection_ack` before they can subscribe. */
  pending: { id: string; channel: string }[];
  acked: boolean;
  lastFrame: number;
  watchers: Set<(state: ChannelState) => void>;
  watchdog: ReturnType<typeof setInterval>;
}

let connection: Connection | undefined;
/**
 * Subscription ids only have to be unique on one connection. A counter rather than
 * `crypto.randomUUID()`, which React Native does not have.
 */
let nextId = 0;

function announce(target: Connection, state: ChannelState): void {
  for (const watcher of target.watchers) watcher(state);
}

function channelPath(channel: string): string {
  return `/${channel.replace(/^\//, '')}`;
}

function sendSubscribe(target: Connection, id: string, channel: string): void {
  target.socket.send(
    JSON.stringify({
      type: 'subscribe',
      id,
      channel: channelPath(channel),
      authorization: { host: target.httpHost, 'x-api-key': target.apiKey },
    }),
  );
}

function defaultSocket(url: string, protocols: string[]): SocketLike {
  const Socket = (globalThis as unknown as { WebSocket?: new (url: string, protocols: string[]) => SocketLike })
    .WebSocket;
  if (!Socket) throw new Error('No WebSocket implementation on this platform.');
  return new Socket(url, protocols);
}

function openConnection(endpoint: RealtimeEndpoint, openSocket: SocketFactory, log: Logger): Connection {
  const host = endpoint.url.replace(/^wss?:\/\//, '');
  const httpHost = endpoint.httpUrl.replace(/^https?:\/\//, '');
  const socket = openSocket(`wss://${host}/event/realtime`, [
    'aws-appsync-event-ws',
    authProtocol(httpHost, endpoint.apiKey),
  ]);

  const created: Connection = {
    socket,
    httpHost,
    apiKey: endpoint.apiKey,
    subscribers: new Map(),
    pending: [],
    acked: false,
    lastFrame: Date.now(),
    watchers: new Set(),
    watchdog: setInterval(() => {
      if (Date.now() - created.lastFrame > KEEPALIVE_GRACE_MS) {
        log.warn('[realtime] no keep-alive; treating the channel as down');
        announce(created, { status: 'error', live: false });
        created.socket.close();
      }
    }, 15_000),
  };

  socket.addEventListener('open', () => {
    created.lastFrame = Date.now();
    socket.send(JSON.stringify({ type: 'connection_init' }));
  });

  socket.addEventListener('message', (event) => {
    let frame: { type?: string; id?: string; event?: string | string[] };
    try {
      frame = JSON.parse(String(event.data)) as typeof frame;
    } catch {
      return;
    }

    // Any frame is proof the connection is alive — the keep-alive arrives every sixty
    // seconds whether or not anybody is publishing.
    created.lastFrame = Date.now();

    switch (frame.type) {
      case 'connection_ack':
        created.acked = true;
        for (const waiting of created.pending.splice(0)) sendSubscribe(created, waiting.id, waiting.channel);
        break;
      case 'subscribe_success':
        // The server confirming a subscription is registered. This — not a published
        // message — is what says the channel works, and a show between lots is silent.
        announce(created, { status: 'open', live: true });
        break;
      case 'data': {
        // The payload is a JSON string inside the frame, and an **array** of them when a
        // publish carried a batch. A single-element array coerces to a string by accident,
        // which works until somebody publishes two.
        const handler = frame.id ? created.subscribers.get(frame.id) : undefined;
        const payloads = frame.event === undefined ? [] : Array.isArray(frame.event) ? frame.event : [frame.event];
        for (const payload of payloads) {
          try {
            handler?.(JSON.parse(payload) as RealtimeMessage);
          } catch {
            // A message this client cannot parse is one from a newer publisher. Dropping it
            // is correct: the poll underneath produces the same state.
          }
        }
        break;
      }
      case 'subscribe_error':
      case 'connection_error':
        announce(created, { status: 'error', live: false });
        break;
      default:
        break;
    }
  });

  socket.addEventListener('error', () => announce(created, { status: 'error', live: false }));
  socket.addEventListener('close', (closed) => {
    // A refused handshake arrives here and nowhere else — there is no error event with a
    // reason in it — so the code and reason are the only diagnosis available.
    log.warn('[realtime] socket closed', { code: closed.code, reason: closed.reason });
    announce(created, { status: 'error', live: false });
    clearInterval(created.watchdog);
    if (connection === created) connection = undefined;
  });

  return created;
}

interface Logger {
  warn: (message: string, detail?: unknown) => void;
}

export interface SubscribeOptions {
  /** Injected by the spec; production uses the platform's `WebSocket`. */
  openSocket?: SocketFactory;
  log?: Logger;
}

/**
 * Subscribe to one channel until the returned function is called.
 *
 * `onState` is called synchronously with `connecting` and then with every change. An
 * `endpoint` of `undefined` is a legitimate deployment — the poll still runs and the screen
 * is still correct — and answers `unconfigured`, loudly, rather than opening nothing
 * silently.
 */
export function subscribeChannel(
  endpoint: RealtimeEndpoint | undefined,
  channel: string,
  onMessage: (message: RealtimeMessage) => void,
  onState: (state: ChannelState) => void,
  options: SubscribeOptions = {},
): () => void {
  const log = options.log ?? console;
  if (!endpoint) {
    onState({ status: 'unconfigured', live: false });
    log.warn(
      '[realtime] no channel: config.json is missing realtimeUrl, realtimeHttpUrl or realtimeApiKey. ' +
        'EdgeStack writes them from RealtimeStack’s SSM parameters. The screen falls back to polling.',
    );
    return () => {};
  }

  nextId += 1;
  const id = `sub-${nextId}`;
  connection ??= openConnection(endpoint, options.openSocket ?? defaultSocket, log);
  const active = connection;

  active.watchers.add(onState);
  active.subscribers.set(id, onMessage);
  onState({ status: 'connecting', live: false });

  if (active.acked) sendSubscribe(active, id, channel);
  else active.pending.push({ id, channel });

  return () => {
    active.watchers.delete(onState);
    active.subscribers.delete(id);
    active.pending = active.pending.filter((waiting) => waiting.id !== id);
    // Unsubscribe before anything else so the server stops counting this subscription.
    if (active.socket.readyState === OPEN) active.socket.send(JSON.stringify({ type: 'unsubscribe', id }));
    // The last subscriber closes the socket: a connection kept open for a screen nobody is
    // looking at is billed by the minute.
    if (active.subscribers.size === 0) {
      clearInterval(active.watchdog);
      active.socket.close();
      if (connection === active) connection = undefined;
    }
  };
}
